import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, XOctagon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { CF_STAGES } from "@/lib/format";

/**
 * BLOCK 61 Item 4 ,  Manual-advance control.
 *
 * The Pipeline Stage <select> above dumps every stage together. Advancing
 * manually into a Manual-mode stage from a select dropdown gives no signal
 * that the transition is a real decision the auto-advance path will not
 * make for the subscriber. This control surfaces the SPECIFIC manual
 * transition available from the current stage as a prominent button, and
 * always exposes "Mark as Dead" separately since that's manual from any
 * live stage.
 *
 * Interaction with auto-advance (backend `pipeline_automation.py`):
 *
 *   - `discovered → scored`         fires on score calc         → auto
 *   - `scored → outreach_sent`      fires on approve-outreach   → auto
 *   - `outreach_sent → engaged`     fires on reply detected     → auto
 *   - `engaged → offer_stage`       MANUAL only                 ← this control
 *   - `offer_stage → under_loi`     fires on LOI doc generated  → auto
 *   - `under_loi → due_diligence`   MANUAL only                 ← this control
 *   - `due_diligence → contract_signed`  fires on contract sent → auto
 *   - `contract_signed → closed`    MANUAL only                 ← this control
 *
 * A manual advance can NOT be silently overwritten by an auto-advance:
 * every auto path checks the CURRENT stage before firing (e.g.
 * `auto_advance_after_scoring` only fires when `cur in (None, "discovered")`).
 * Once the subscriber is past that gate, no automated path will pull them
 * back or push them past a manual step they haven't yet taken.
 */
export default function ManualStageAdvanceControl({ currentStage, oid, onAdvanced }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  // Find the immediate next stage in the CF_STAGES progression.
  const idx = CF_STAGES.findIndex((s) => s.value === currentStage);
  const nextStage = idx >= 0 && idx < CF_STAGES.length - 1 ? CF_STAGES[idx + 1] : null;
  const nextIsManual = !!nextStage && nextStage.mode === "manual" && nextStage.value !== "dead";

  // Dead is always manual from any live stage
  const canMarkDead = !!currentStage && currentStage !== "dead" && currentStage !== "closed";

  if (!currentStage || (!nextIsManual && !canMarkDead)) return null;

  const advanceTo = async (targetStage, targetLabel) => {
    setBusy(true);
    try {
      const body = { stage: targetStage };
      if (reason.trim()) body.reason = reason.trim();
      const r = await api.post(`/pipeline-v2/${oid}/advance`, body);
      toast.success(`Advanced to ${targetLabel}`);
      setReason("");
      onAdvanced?.(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        detail?.error === "upgrade_required"
          ? "Upgrade to Pro to advance beyond scoring."
          : (typeof detail === "string" ? detail : detail?.message || "Manual advance failed")
      );
    } finally {
      setBusy(false);
    }
  };

  const markDead = async () => {
    setBusy(true);
    try {
      await api.post(`/pipeline-v2/${oid}/mark-dead`, { reason: reason.trim() || "subscriber_marked_dead" });
      toast.success("Marked as dead");
      setReason("");
      onAdvanced?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Mark dead failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-3 p-3 border border-violet-500/30 bg-violet-500/5 rounded-sm space-y-2"
      data-testid="manual-advance-control"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-violet-300/80">
          Manual Advance {nextIsManual && <span className="text-slate-500 ml-2">,  auto-advance won't fire</span>}
        </div>
      </div>

      <input
        type="text"
        placeholder="Reason (optional ,  e.g. 'buyer verbal accept')"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={140}
        data-testid="manual-advance-reason"
        className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-1.5 text-xs"
        disabled={busy}
      />

      <div className="flex flex-col gap-2">
        {nextIsManual && (
          <button
            onClick={() => advanceTo(nextStage.value, nextStage.label)}
            disabled={busy}
            data-testid="manual-advance-next-btn"
            className="w-full inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-sm bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/40 text-violet-100 font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            Advance to <span className="font-semibold">{nextStage.label}</span>
          </button>
        )}

        {canMarkDead && (
          <button
            onClick={markDead}
            disabled={busy}
            data-testid="manual-mark-dead-btn"
            className="w-full inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-sm bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 disabled:opacity-50"
          >
            <XOctagon className="h-3.5 w-3.5" />
            Mark as Dead
          </button>
        )}
      </div>
    </div>
  );
}
